/** The AI checklist: 11 factors + trade terms, statuses, and the answer step (original CK, stLab, runAI/showQuestions logic). */
import { L, type Answer, type Items, type Lang, type Question } from "./types.ts";

export const CK: [string, string, string][] = [
  ["category", "Category / sub-category", "品类 / 子类"],
  ["main_fabric", "Main fabric spec + source", "主面料规格 + 来源"],
  ["usage", "Fabric usage", "用量"],
  ["sizes", "Sizes / size range", "码数 / 尺码范围"], // 偏差 D9: 6–8 sizes is normal; more (or 3XL+) → AI weighs yield and CM
  ["other_fabrics", "Other fabrics", "其他面料"],
  ["trims", "Trims & packaging", "辅料和包装"],
  ["complexity", "Construction complexity → SAM", "工艺复杂度 → SAM"],
  ["special_process", "Special processes", "特殊工序"],
  ["qty", "Order qty + colours", "订单量 + 颜色数"],
  ["compliance", "Compliance (testing / inspection)", "合规要求（检测 / 验货）"],
  ["origin", "Production location", "生产地"],
  ["lead_time", "Lead time / rush", "交期 / 加急"],
  ["trade_terms", "Trade terms / destination (Quote only)", "贸易条款 / 目的地（仅 Quote）"],
];

/** JF 2026-09-29: up to 6 questions; trade terms / destination are never asked (costing stage, not the quote). */
export const MAX_QUESTIONS = 6;
export const NEVER_ASK = new Set(["trade_terms"]);
/** Number of checklist factors (trade terms is the "+" extra for Quote). */
export const CK_FACTORS = CK.filter(([k]) => k !== "trade_terms").length;

/** Size-count judgement shown next to the sizes (the AI prices it; there is no fixed coefficient — JF 2026-09-29). */
export function sizeBand(count: unknown): "few" | "normal" | "many" | null {
  const c = Math.round(Number(count));
  if (!Number.isFinite(c) || c <= 0) return null;
  return c <= 5 ? "few" : c <= 8 ? "normal" : "many";
}

/** Status → [label, css class]. */
export function statusLabel(status: unknown, lang: Lang): [string, string] {
  const m: Record<string, [string, string]> = {
    got: [L(lang, "read", "已读到"), "got"],
    assumed: [L(lang, "AI assumed", "AI 假设"), "asm"],
    ask: [L(lang, "to answer", "待回答"), "ask"],
    answered: [L(lang, "you answered", "你已回答"), "ans"],
    edited: [L(lang, "edited by you", "你已修改"), "edt"],
    missing: [L(lang, "unknown", "未知"), "pend"],
  };
  return m[String(status)] || [L(lang, "waiting", "待读取"), "pend"];
}

export interface ChecklistRow { key: string; no: string; name: string; status: [string, string]; value: string; note: string }

export function checklistRows(items: Items | null | undefined, lang: Lang): ChecklistRow[] {
  return CK.map(([key, en, zh], i) => {
    const it = (items || {})[key] || {};
    return { key, no: key === "trade_terms" ? "+" : String(i + 1), name: L(lang, en, zh), status: statusLabel(it.status, lang), value: it.value ? String(it.value) : "", note: it.note ? String(it.note) : "" };
  });
}

/**
 * After extraction: make sure every key exists, turn "missing" into "assumed" when not asking,
 * drop questions on keys we never ask, cap the questions at MAX_QUESTIONS (none when not asking) and mark asked keys "ask".
 */
export function prepareItems(items: Items, questions: Question[] | undefined, ask: boolean): { items: Items; questions: Question[] } {
  const qs = (ask ? questions || [] : []).filter((q) => !NEVER_ASK.has(q.key)).slice(0, MAX_QUESTIONS);
  for (const [k] of CK) {
    const it = (items[k] = items[k] || { value: "", status: "missing" });
    if (!ask && it.status === "missing") it.status = "assumed";
  }
  for (const q of qs) if (items[q.key]) items[q.key].status = "ask";
  return { items, questions: qs };
}

/** Label of an option in the UI language. */
export const optionLabel = (o: { v: string; label_en?: string; label_zh?: string }, lang: Lang): string =>
  lang === "zh" ? o.label_zh || o.label_en || o.v : o.label_en || o.label_zh || o.v;

/** Synchronize confirmed answers before building history context. Unparsed replacements
 * stay in value/AM ANSWERS; old structured guesses must not contradict them. */
export function applyAnswers(items: Items, answers: Record<string, Answer>, lang: Lang): void {
  for (const [key, answer] of Object.entries(answers)) {
    const item = items[key];
    if (!item || !answer || typeof answer !== "object" || typeof answer.v !== "string") continue; // unknown/legacy answers stay provisional
    const text = answer.label || answer.v;
    item.value = text;
    item.status = "answered";
    item.note = L(lang, "Confirmed by your answer", "根据你的回答更新");
    const combined = `${answer.v} ${text}`;
    if (key === "sizes") {
      const count = /\d\s*[-–—~至到]\s*\d/.test(text) ? null : text.match(/(?:^|[^\d])(\d+)\s*(?:码|個尺碼|个尺码|sizes?\b)/i)
        || answer.v.match(/^(\d+)$/);
      const names = text.toUpperCase().match(/\b(?:XXXS|XXS|XS|S|M|L|XL|XXL|XXXL|[2-9]XL)\b/g) || [];
      const order = ["XXXS", "XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL", "4XL", "5XL", "6XL", "7XL", "8XL", "9XL"];
      const canonical = names.map(n => n === "2XL" ? "XXL" : n === "3XL" ? "XXXL" : n);
      let list = [...new Set(canonical)];
      if (list.length === 2 && /[-–—~至到]/.test(text)) {
        const start = order.indexOf(list[0]), end = order.indexOf(list[1]);
        if (start >= 0 && end >= start) list = order.slice(start, end + 1);
      }
      item.count = count ? Number(count[1]) : list.length || null;
      item.list = list.length === item.count ? list : [];
      item.extended = canonical.length ? canonical.some(n => order.indexOf(n) >= order.indexOf("XXXL")) : null;
    } else if (key === "qty") {
      const pair = answer.v.match(/^(\d+)\|(\d+)$/);
      const pcs = combined.match(/([\d,]+)\s*(?:pcs\b|pieces\b|件)/i) || answer.v.match(/^(\d+)$/);
      const colors = combined.match(/(\d+)\s*(?:colou?rs?\b|色)/i);
      item.pcs = pair ? Number(pair[1]) : pcs ? Number(pcs[1].replaceAll(",", "")) : null;
      item.colors = pair ? Number(pair[2]) : colors ? Number(colors[1]) : null;
      item.tiers = [];
    } else if (key === "origin") {
      const pair = answer.v.split("|");
      item.city = pair.length === 2 ? pair[0] : "";
      item.country = pair.length === 2 ? pair[1] : /^(China|Vietnam|Cambodia|Bangladesh|India|中国|越南|柬埔寨|孟加拉|印度)$/i.test(text) ? text : "";
    } else if (key === "lead_time") {
      item.ex_factory = "";
      item.rush = /^(standard|normal)$/i.test(answer.v) || /标准交期|正常交期/.test(text) ? false
        : /^(rush|urgent)$/i.test(answer.v) || /加急/.test(text) ? true : null;
    } else if (key === "compliance") {
      if (/^(none|basic|full)$/.test(answer.v)) {
        item.testing = answer.v;
        if (answer.v === "none") item.inspection = "none";
      } else {
        // Free text is authoritative; avoid carrying a previous paid test/inspection default.
        item.testing = /(?:不需要|无需|不做|不用).*?(?:测试|检测)|no\s+(?:testing|tests?)/i.test(text) ? "none" : null;
        item.inspection = /(?:不需要|无需|不做|不用).*?(?:验货|检验)|no\s+inspection/i.test(text) ? "none" : null;
      }
    } else if (key === "main_fabric") {
      if (/^(stock|custom|nominated|client_supplied)$/.test(answer.v)) item.source = answer.v;
      else {
        for (const field of ["composition", "construction", "width", "source", "supplier", "article", "fabric_origin"]) item[field] = "";
        item.gsm = null;
      }
    } else if (key === "usage") {
      item.m_per_pc = null; item.raw_qty = null; item.raw_unit = "";
    } else if (key === "complexity") {
      item.level = ""; item.sam_est = null;
    }
  }
}

/**
 * Record the AM's answers. `picks[qi]` = option value, "__dk" (don't know) or free text via `free[qi]`.
 * Unanswered = don't know → item "assumed".
 */
export function answerQuestions(items: Items, questions: Question[], picks: (string | null | undefined)[], free: (string | null | undefined)[], lang: Lang): Record<string, Answer> {
  const answers: Record<string, Answer> = {};
  questions.forEach((q, qi) => {
    const fr = String(free[qi] || "").trim();
    const opt = (q.options || []).find((o) => o.v === picks[qi]);
    const v = fr || (picks[qi] && picks[qi] !== "__dk" ? String(picks[qi]) : "__dk");
    const lab = fr || (opt ? optionLabel(opt, lang) : "");
    answers[q.key] = v === "__dk" ? null : { v, label: lab };
    const it = items[q.key];
    if (it) {
      if (v === "__dk") { it.status = "assumed"; it.note = L(lang, "AI assumed: you didn't know", "AI 假设：你选了不知道"); }
      else { it.status = "answered"; it.value = lab; }
    }
  });
  applyAnswers(items, answers, lang);
  return answers;
}
